import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import useGetFinancialTransaction from '@/features/dashboard/financialTransactions/hooks/useGetFinancialTransaction.ts';
import {
  isBookingAllocation,
  TransactionAllocationDto,
} from '@/features/dashboard/financialTransactions/types/TransactionAllocation.ts';
import { getTransactionAllocationTypeLabel } from '@/features/dashboard/financialTransactions/types/TransactionAllocationType.utils.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import {
  isZeroMoney,
  subtractMoney,
  sumMoney,
} from '@/features/dashboard/bookingChanges/utils/money.ts';

// Najechanie kursorem z ikony na treść przechodzi przez szczelinę `sideOffset`;
// bez zwłoki popover zamykałby się, zanim kursor dotrze do linku.
const HOVER_CLOSE_DELAY_MS = 150;

const rowClass = 'flex items-baseline justify-between gap-4 py-1.5 text-sm';
const amountClass = 'font-semibold tabular-nums text-ink';

const AllocationLabel = ({
  allocation,
  bookingId,
}: {
  allocation: TransactionAllocationDto;
  bookingId: string;
}) => {
  if (!isBookingAllocation(allocation)) {
    return (
      <span className="text-neutral-700">
        {getTransactionAllocationTypeLabel(allocation.type)}
      </span>
    );
  }
  if (allocation.bookingId === bookingId) {
    return <span className="font-semibold text-ink">Ta rezerwacja</span>;
  }
  return (
    <Link
      to="/app/bookings/$bookingId/booking"
      params={{ bookingId: allocation.bookingId }}
      className="inline-flex items-center gap-1 text-neutral-700 underline-offset-2 hover:underline"
    >
      Inna rezerwacja
      <ArrowUpRight aria-hidden className="size-3.5" />
    </Link>
  );
};

/** Reszta przelewu bez przypisania — liczona tylko w walucie przelewu. */
const unallocatedOf = (
  total: MoneyValueDto,
  allocations: TransactionAllocationDto[],
): MoneyValueDto | null => {
  const allocated = sumMoney(
    allocations
      .map((allocation) => allocation.amount)
      .filter((amount) => amount.currency === total.currency),
  )[0];
  const rest = allocated ? subtractMoney(total, allocated) : total;
  return isZeroMoney(rest) ? null : rest;
};

const SplitSkeleton = () => (
  <div className="flex flex-col gap-2.5 py-1">
    {Array.from({ length: 3 }, (_, index) => (
      <div key={index} className="flex justify-between gap-4">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-3.5 w-20" />
      </div>
    ))}
  </div>
);

const SplitContent = ({
  transactionId,
  bookingId,
}: {
  transactionId: string;
  bookingId: string;
}) => {
  const { data, isLoading, isError } =
    useGetFinancialTransaction(transactionId);

  if (isError) {
    return <LoadError subject="podziału przelewu" />;
  }
  if (isLoading || !data) {
    return <SplitSkeleton />;
  }

  const unallocated = unallocatedOf(data.amount, data.allocations);

  return (
    <div className="flex flex-col">
      <ul className="flex flex-col border-b border-dashed border-neutral-200 pb-1.5">
        {data.allocations.map((allocation, index) => (
          <li key={allocation.id ?? index} className={rowClass}>
            <AllocationLabel allocation={allocation} bookingId={bookingId} />
            <MoneyAmountList
              className={amountClass}
              values={[allocation.amount]}
            />
          </li>
        ))}
        {unallocated && (
          <li className={rowClass}>
            <span className="text-neutral-700">Nieprzypisane</span>
            <MoneyAmountList className={amountClass} values={[unallocated]} />
          </li>
        )}
      </ul>
      <div className={cn(rowClass, 'pt-2.5')}>
        <span className="font-semibold text-ink">Cały przelew</span>
        <MoneyAmountList className={amountClass} values={[data.amount]} />
      </div>
    </div>
  );
};

/**
 * Znacznik przy kwocie wpłaty, której przelew nie przypadł w całości tej
 * rezerwacji. Podział czyta dopiero po otwarciu — lista wpłat go nie niesie,
 * a większość wierszy znacznika w ogóle nie ma.
 */
const PaymentSplitPopover = ({
  transactionId,
  bookingId,
}: {
  transactionId: string;
  bookingId: string;
}) => {
  const [open, setOpen] = useState(false);
  // Kliknięty popover zostaje otwarty po zjechaniu kursorem — zamyka go dopiero
  // klik obok albo Escape, jak każdy inny.
  const [pinned, setPinned] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const openNow = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (pinned) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="Przelew nie w całości przypisany do tej rezerwacji"
        onClick={(event) => {
          // Bez tego Radix przełączyłby popover otwarty już najechaniem z powrotem na zamknięty.
          event.preventDefault();
          if (pinned && open) {
            setOpen(false);
            return;
          }
          setPinned(true);
          openNow();
        }}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        className="inline-flex shrink-0 rounded-sm text-warning-100 outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
      >
        <TriangleAlert aria-hidden className="size-4" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        // Otwarty samym najechaniem nie może zabierać fokusu z miejsca, w którym
        // operator akurat jest — ani przy otwarciu, ani przy zamknięciu.
        onOpenAutoFocus={(event) => {
          if (!pinned) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (!pinned) event.preventDefault();
          setPinned(false);
        }}
        className="w-80 rounded-md px-4 py-3"
      >
        <p className={cn(columnLabelClass, 'pb-1.5')}>Podział przelewu</p>
        {open && (
          <SplitContent transactionId={transactionId} bookingId={bookingId} />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default PaymentSplitPopover;
